/**
 * Client Task Queue Service
 * 
 * Polls the client_task_queue for pending tasks that this platform can execute.
 * When a task is found, it claims it, executes it, and reports the result.
 * 
 * This enables the Intelligent Message Router to dispatch tasks to any
 * online platform (iOS, Android, Web, Orchestrator) for execution.
 */

import { createClient } from '@/lib/supabase/client';
import type { SupabaseClient } from '@supabase/supabase-js';

type ClientTask = {
    id: string;
    user_id: string;
    skill_id: string;
    intent: string;
    command: string;
    parameters: Record<string, any>;
    source_platform: string;
    source_chat_id: string | null;
    target_distro: 'any' | 'ios' | 'android' | 'web' | 'orchestrator' | 'pending';
    priority: number;
    status: string;
    created_at: string;
    should_notify_source?: boolean;
};

type TaskResult = {
    success: boolean;
    response?: string;
    data?: any;
    error?: string;
};

// Skill handler function type
type SkillHandler = (task: ClientTask) => Promise<TaskResult>;

class ClientTaskQueueService {
    private supabase: SupabaseClient;
    private platform: 'web' | 'ios' | 'android' = 'web';
    private deviceId: string = '';
    private pollingInterval: ReturnType<typeof setInterval> | null = null;
    private isPolling = false;
    // W5 audit fix: Re-entrancy guard to prevent concurrent checkForTasks execution
    private isCheckingTasks = false;
    private skillHandlers: Map<string, SkillHandler> = new Map();

    constructor() {
        this.supabase = createClient();
        this.deviceId = this.getDeviceId();
        this.registerDefaultHandlers();
    }

    /**
     * Get or generate a unique device ID
     */
    private getDeviceId(): string {
        if (typeof window === 'undefined') return 'server';

        try {
            let deviceId = localStorage.getItem('linkbox_device_id');
            if (!deviceId) {
                deviceId = crypto.randomUUID();
                localStorage.setItem('linkbox_device_id', deviceId);
            }
            return deviceId;
        } catch (e) {
            // iOS Safari Private Mode might throw an error when accessing localStorage
            console.warn("localStorage is restricted, falling back to ephemeral device ID");
            return crypto.randomUUID();
        }
    }

    /**
     * Register a handler for a specific skill
     */
    registerSkillHandler(skillId: string, handler: SkillHandler): void {
        this.skillHandlers.set(skillId, handler);
        console.log(`[TaskQueue] Registered handler for skill: ${skillId}`);
    }

    /**
     * Register default handlers for common web skills
     */
    private registerDefaultHandlers(): void {
        // Example: Browser notification handler
        this.registerSkillHandler('browser_notification', async (task) => {
            if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('LinkBox Task', { body: task.command });
                return { success: true, response: 'Notification shown' };
            }
            return { success: false, error: 'Notifications not available' };
        });

        // Example: Clipboard handler
        this.registerSkillHandler('clipboard_copy', async (task) => {
            try {
                await navigator.clipboard.writeText(task.parameters?.text || task.command);
                return { success: true, response: 'Copied to clipboard' };
            } catch (e) {
                return { success: false, error: 'Clipboard access denied' };
            }
        });

        // Example: Open URL handler
        this.registerSkillHandler('open_url', async (task) => {
            const url = task.parameters?.url || task.command;
            window.open(url, '_blank');
            return { success: true, response: `Opened: ${url}` };
        });

        // Default handler for unknown skills
        this.registerSkillHandler('default', async (task) => {
            console.log(`[TaskQueue] No specific handler for skill: ${task?.skill_id || 'null'}`);
            return {
                success: false,
                error: `Skill '${task?.skill_id || 'unknown'}' not supported on this platform`
            };
        });
    }

    /**
     * Start polling for tasks
     */
    startPolling(intervalMs: number = 5000): void {
        if (this.isPolling) {
            console.log('[TaskQueue] Already polling');
            return;
        }

        this.isPolling = true;
        console.log(`[TaskQueue] Starting polling every ${intervalMs}ms`);

        // Update device last_seen
        this.updateDeviceStatus();

        // Initial check
        this.checkForTasks();

        // Start interval
        this.pollingInterval = setInterval(() => {
            this.checkForTasks();
            this.updateDeviceStatus();
        }, intervalMs);
    }

    /**
     * Stop polling for tasks
     */
    stopPolling(): void {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
        this.isPolling = false;
        console.log('[TaskQueue] Stopped polling');
    }

    /**
     * Update device online status
     */
    private async updateDeviceStatus(): Promise<void> {
        try {
            const { data: { user } } = await this.supabase.auth.getUser();
            if (!user) return;

            const { error } = await this.supabase
                .from('user_devices')
                .upsert({
                    user_id: user.id,
                    device_id: this.deviceId,
                    device_type: this.platform, // Changed from 'platform' to 'device_type'
                    device_name: `Web Browser`,
                    is_active: true,
                    last_seen: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                }, {
                    onConflict: 'user_id,device_id'
                });

            if (error) {
                // Silently handle - device registration is non-critical
                console.debug('[TaskQueue] Device status update skipped:', error.message);
            }
        } catch (e) {
            // Don't break polling loop for device registration errors
            console.debug('[TaskQueue] Device status update error:', e);
        }
    }

    /**
     * Check for and claim pending tasks
     */
    private async checkForTasks(): Promise<void> {
        // W5 audit fix: Re-entrancy guard — if a previous check is still running
        // (e.g., task execution takes longer than the polling interval),
        // skip this invocation to prevent duplicate task claims.
        if (this.isCheckingTasks) {
            console.debug('[TaskQueue] Skipping overlapping checkForTasks — previous check still running');
            return;
        }

        this.isCheckingTasks = true;
        try {
            const { data: { user } } = await this.supabase.auth.getUser();
            if (!user) return;

            // Claim a task using the atomic RPC function
            const { data: task, error } = await this.supabase
                .rpc('claim_client_task', {
                    p_user_id: user.id,
                    p_distro: this.platform,
                    p_device_id: this.deviceId
                });

            if (error) {
                // Check for empty error objects or non-critical errors
                const errorMessage = error?.message || '';
                const errorCode = error?.code || '';

                // Skip logging for empty errors or common non-critical cases
                if (!errorMessage && !errorCode && Object.keys(error).length === 0) {
                    // Empty error object - likely function returned null/undefined
                    return;
                }

                // Only log actual errors
                console.debug('[TaskQueue] Task claim returned:', errorMessage || 'no tasks');
                return;
            }

            if (!task) {
                // No pending tasks
                return;
            }

            // Guard against RPC returning an object with null id
            if (!task.id) {
                // Task object exists but has no valid ID - skip execution
                return;
            }

            console.log(`[TaskQueue] Claimed task: ${task.id} (${task.skill_id})`);

            // Execute the task
            await this.executeTask(task as ClientTask);

        } catch (e) {
            // Suppress AbortErrors (occur during navigation/cleanup)
            if (e instanceof Error) {
                if (e.name === 'AbortError' || e.message?.includes('aborted')) {
                    // Normal during page navigation, suppress
                    return;
                }
            }
            // Only log actual unexpected errors
            console.debug('[TaskQueue] Task check error:', e);
        } finally {
            this.isCheckingTasks = false;
        }
    }

    /**
     * Execute a claimed task
     */
    private async executeTask(task: ClientTask): Promise<void> {
        // Guard against null/undefined task or task.id
        if (!task || !task.id) {
            console.warn('[TaskQueue] Attempted to execute task with invalid ID:', task?.id);
            return;
        }

        console.log(`[TaskQueue] Executing task: ${task.skill_id}`);

        // Update status to executing
        await this.supabase
            .from('client_task_queue')
            .update({ status: 'executing' })
            .eq('id', task.id);

        // Get handler
        const handler = this.skillHandlers.get(task.skill_id)
            || this.skillHandlers.get('default')!;

        try {
            // Execute
            const result = await handler(task);

            // Report result
            await this.supabase.rpc('complete_client_task', {
                p_task_id: task.id,
                p_result: result,
                p_success: result.success,
                p_error_message: result.error || null
            });

            console.log(`[TaskQueue] Task ${task?.id || 'unknown'} completed:`, result.success);

            // If source platform was messaging, send response back
            if (task.should_notify_source && task.source_chat_id) {
                await this.notifySourcePlatform(task, result);
            }

        } catch (e: any) {
            console.error(`[TaskQueue] Task ${task.id} failed:`, e);

            await this.supabase.rpc('complete_client_task', {
                p_task_id: task.id,
                p_result: { error: e.message },
                p_success: false,
                p_error_message: e.message
            });
        }
    }

    /**
     * Notify the source messaging platform of the result
     * This completes the feedback loop - when a voice/messaging command triggers
     * a task that runs on another device, the result is sent back to the user
     * on their original platform.
     */
    private async notifySourcePlatform(task: ClientTask, result: TaskResult): Promise<void> {
        if (!task.source_platform || !task.source_chat_id) {
            console.log(`[TaskQueue] No source platform to notify for task ${task.id}`);
            return;
        }

        console.log(`[TaskQueue] Notifying ${task.source_platform}:`, result.response);

        try {
            // Format the message based on success/failure
            const statusEmoji = result.success ? '✅' : '❌';
            const statusText = result.success ? 'completed' : 'failed';
            const message = [
                `${statusEmoji} **Task ${statusText}**: ${task.skill_id || 'Task'}`,
                '',
                result.response || result.error || 'No details available',
                '',
                `📋 Original: "${(task.command || '').substring(0, 50)}${(task.command?.length || 0) > 50 ? '...' : ''}"`,
            ].join('\n');

            // Call the appropriate messaging gateway to send the response back
            const gatewayName = `${task.source_platform}-gateway`;
            const { error } = await this.supabase.functions.invoke(gatewayName, {
                body: {
                    action: 'send_message',
                    chat_id: task.source_chat_id,
                    message: message,
                    // Include metadata for potential rich formatting
                    metadata: {
                        task_id: task.id,
                        skill_id: task.skill_id,
                        success: result.success,
                        timestamp: new Date().toISOString()
                    }
                }
            });

            if (error) {
                console.error(`[TaskQueue] Failed to notify ${task.source_platform}:`, error);
            } else {
                console.log(`[TaskQueue] Successfully notified ${task.source_platform}`);
            }
        } catch (e) {
            console.error(`[TaskQueue] Error notifying source platform:`, e);
        }
    }

    /**
     * Manually execute a task by ID (for testing or manual triggering)
     */
    async executeTaskById(taskId: string): Promise<TaskResult> {
        // Guard against null/undefined taskId
        if (!taskId || taskId === 'null' || taskId === 'undefined') {
            console.warn('[TaskQueue] Attempted to execute task with invalid ID:', taskId);
            return { success: false, error: 'Invalid task ID' };
        }

        const { data: task, error } = await this.supabase
            .from('client_task_queue')
            .select('*')
            .eq('id', taskId)
            .single();

        if (error || !task) {
            return { success: false, error: 'Task not found' };
        }

        // Claim it
        await this.supabase
            .from('client_task_queue')
            .update({
                status: 'claimed',
                claimed_by_distro: this.platform,
                claimed_by_device_id: this.deviceId,
                claimed_at: new Date().toISOString()
            })
            .eq('id', taskId);

        // Execute
        const handler = this.skillHandlers.get(task.skill_id)
            || this.skillHandlers.get('default')!;

        return await handler(task as ClientTask);
    }

    /**
     * Get pending tasks for this user (for UI display)
     */
    async getPendingTasks(): Promise<ClientTask[]> {
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await this.supabase
            .from('client_task_queue')
            .select('*')
            .eq('user_id', user.id)
            .in('status', ['pending', 'awaiting_choice'])
            .order('created_at', { ascending: false })
            .limit(20);

        if (error) {
            console.error('[TaskQueue] Error fetching pending tasks:', error);
            return [];
        }

        return (data || []) as ClientTask[];
    }
}

// Singleton instance
export const clientTaskQueueService = new ClientTaskQueueService();

// React hook for easy usage
export function useClientTaskQueue() {
    return {
        startPolling: (intervalMs?: number) => clientTaskQueueService.startPolling(intervalMs),
        stopPolling: () => clientTaskQueueService.stopPolling(),
        registerHandler: (skillId: string, handler: SkillHandler) =>
            clientTaskQueueService.registerSkillHandler(skillId, handler),
        getPendingTasks: () => clientTaskQueueService.getPendingTasks(),
        executeTask: (taskId: string) => clientTaskQueueService.executeTaskById(taskId)
    };
}
